<script setup lang="ts">
import type { IconKeys } from "../Icon/Icon.interface"
import { computed, useSlots } from "vue"
import { Icon } from "@/components/ui"

interface BannerProps {
  title?: string
  description?: string
  prefixIcon?: IconKeys
  variant?: "default" | "success" | "warning" | "danger"
  closable?: boolean
}

const props = withDefaults(defineProps<BannerProps>(), {
  variant: "default",
})

const emit = defineEmits(["close"])

const slots = useSlots()

const defaultIcon = computed<IconKeys>(() => {
  if (props.variant === "default")
    return "CircleInfo"
  if (props.variant === "success")
    return "Check"
  if (props.variant === "warning")
    return "Caution"
  if (props.variant === "danger")
    return "Caution"
  return "CircleInfo"
})
</script>

<template>
  <div
    class="rounded-lg font-montserrat py-2 px-4"
    :class="{
      'bg-[#EAF4FB] text-[#004B87]': variant === 'default',
      'bg-[#E6F4EA] text-[#205C3B]': variant === 'success',
      'bg-[#FFF4CE] text-[#664400]': variant === 'warning',
      'bg-[#FEEBE6] text-[#7A3E2E]': variant === 'danger',
    }"
  >
    <div
      class="flex justify-between gap-2 md:gap-4 items-center text-sm md:text-base"
      :class="{
        'flex-col items-stretch sm:items-center sm:flex-row': slots.actions,
      }"
    >
      <div class="flex gap-2">
        <slot name="icon">
          <Icon :icon="prefixIcon || defaultIcon" size="sm" class="shrink-0" />
        </slot>
        <div>
          <h3 v-if="title" class="font-semibold">
            {{ title }}
          </h3>
          <slot name="description">
            <p v-if="description" class="text-xs md:text-sm">
              {{ description }}
            </p>
          </slot>
        </div>
      </div>

      <div>
        <slot name="actions">
          <div v-if="closable" class="flex items-center gap-2">
            <button data-test="close-banner" type="button" class="cursor-pointer" @click="emit('close')">
              <Icon icon="Close" size="sm" />
            </button>
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>
